import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, BookOpen, Tag, Printer } from 'lucide-react';

export const ArticleDetail = () => {
  const { id } = useParams();
  const { API, token } = useAuth();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-[#A8A29E]">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="text-center py-12">
        <p className="text-[#57534E]">Article not found</p>
        <Button onClick={() => navigate('/knowledge-base')} className="mt-4">
          Back to Knowledge Base
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="article-detail">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/knowledge-base')}
        variant="ghost"
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Knowledge Base
      </Button>

      {/* Article Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8">
        <div className="flex items-start gap-4 mb-4">
          <BookOpen className="w-12 h-12 text-primary flex-shrink-0" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] leading-tight">
            {article.title}
          </h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
            article.category === 'PANDAS/PANS' ? 'bg-primary/20 text-primary' :
            article.category === 'Mycoplasma Pneumonia' ? 'bg-accent/20 text-accent' :
            article.category === 'Histamine Intolerance' ? 'bg-secondary text-secondary-foreground' :
            'bg-primary/30 text-primary'
          }`}>
            {article.category}
          </span>
          <Button
            onClick={handlePrint}
            variant="ghost"
            className="text-primary hover:text-primary/80"
            data-testid="print-button"
          >
            <Printer className="w-5 h-5 mr-2" />
            Print Guide
          </Button>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        {article.tags.map((tag, index) => (
          <span
            key={index}
            className="px-3 py-1 bg-white border border-stone-200 rounded-full text-sm font-medium text-[#57534E] flex items-center gap-1"
          >
            <Tag className="w-3 h-3" />
            {tag}
          </span>
        ))}
      </div>

      {/* Article Content */}
      <Card className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8 md:p-12">
        <div className="prose prose-lg max-w-none prose-headings:font-nunito prose-headings:text-[#2D2D2D] prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:font-semibold prose-p:text-[#57534E] prose-p:leading-relaxed prose-strong:text-[#2D2D2D] prose-ul:text-[#57534E] prose-li:text-[#57534E] prose-em:text-[#78716C]">
          <ReactMarkdown
            components={{
              hr: () => (
                <hr className="my-8 border-t-2 border-primary/20" />
              ),
              img: ({ src, alt }) => (
                <figure className="my-6">
                  <img 
                    src={src} 
                    alt={alt || 'Article illustration'} 
                    className="rounded-lg shadow-md max-w-full h-auto mx-auto"
                    loading="lazy"
                  />
                  {alt && <figcaption className="text-center text-sm text-[#78716C] mt-2 italic">{alt}</figcaption>}
                </figure>
              ),
              ul: ({ children }) => (
                <ul className="space-y-3 my-4">{children}</ul>
              ),
              li: ({ children }) => (
                <li className="text-[#57534E] leading-relaxed">{children}</li>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-primary/30 pl-4 italic text-[#78716C] my-4">{children}</blockquote>
              )
            }}
          >
            {article.content}
          </ReactMarkdown>

          {/* Sections */}
          {article.sections && article.sections.length > 0 && (
            <div className="space-y-8 mt-8">
              {article.sections.map((section, index) => (
                <div key={index} className="border-l-4 border-primary pl-6">
                  <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-4">
                    {section.title}
                  </h2>
                  <div className="prose prose-lg max-w-none prose-p:text-[#57534E] prose-strong:text-[#2D2D2D]">
                    <ReactMarkdown>{section.content}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Related Actions */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/10 rounded-xl border border-stone-100 p-8">
        <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">
          Found this helpful?
        </h3>
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={() => navigate('/tracker')}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3 font-semibold"
            data-testid="track-symptoms-button"
          >
            Track Symptoms & Triggers
          </Button>
          <Button
            onClick={() => navigate('/chat')}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full px-6 py-3 font-semibold"
            data-testid="ask-ai-button"
          >
            Ask AI Assistant
          </Button>
          <Button
            onClick={() => navigate('/forum')}
            variant="outline"
            className="rounded-full px-6 py-3 font-semibold border-primary text-primary hover:bg-primary/10"
            data-testid="join-forum-button"
          >
            Join Discussion
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ArticleDetail;
